/**
 * Ask-AI / Build-with-AI (S9) — pure prompt building + response parsing.
 * No IPC, no React: `hooks/useDashboardAiPropose.ts` is the only caller.
 * The AI is instructed to respond with ONLY a fenced JSON block; nothing here
 * executes SQL — proposals are inert data until the caller applies/adds one.
 */

/** Tile types the AI may propose. 'note' is never proposed — it carries no query. */
export type AiTileProposalType = 'chart' | 'stat' | 'table' | 'pivot';

export type TileProposal = {
  id: string;
  title: string;
  tileType: AiTileProposalType;
  sql: string;
};

export type DashboardAiSchemaContext = {
  tables: string[];
  truncated: boolean;
};

export type DashboardAiRequestArgs = {
  connectionAlias: string;
  engine: string;
  database?: string;
  schemaContext: DashboardAiSchemaContext;
  prompt: string;
  /** Single-tile pane proposes exactly one; the panel proposes several. */
  mode: 'single' | 'multi';
};

const VALID_TILE_TYPES: ReadonlySet<string> = new Set(['chart', 'stat', 'table', 'pivot']);

/** Builds the system prompt + user message sent to the AI CLI adapter. */
export function buildDashboardAiRequest(args: DashboardAiRequestArgs): {
  systemPrompt: string;
  message: string;
} {
  const tileCountInstruction =
    args.mode === 'single'
      ? 'Propose exactly ONE tile.'
      : 'Propose between 2 and 5 tiles that together cover the request.';
  const tableList = args.schemaContext.tables.length
    ? args.schemaContext.tables.map((t) => `- ${t}`).join('\n')
    : '(no tables found)';
  const truncationNote = args.schemaContext.truncated
    ? ' (truncated — more tables exist but are not shown)'
    : '';

  const systemPrompt = [
    'You are an AI assistant embedded in Jam SQL Studio, helping the user design SQL dashboard tiles.',
    `Restricted context — you may ONLY reference this ONE connection: "${args.connectionAlias}" (engine: ${args.engine}${args.database ? `, database: "${args.database}"` : ''}). Never reference any other connection or database.`,
    `Tables available on this connection${truncationNote}:\n${tableList}`,
    `${tileCountInstruction} Every tile query MUST be a single read-only SELECT statement — no writes, no DDL, no multiple statements, no comments about execution.`,
    'Respond with ONLY one fenced ```json code block and nothing else (no prose before or after) containing exactly this shape:\n' +
      '{ "proposals": [ { "title": string, "tileType": "chart" | "stat" | "table" | "pivot", "sql": string } ] }',
  ].join('\n\n');

  return { systemPrompt, message: args.prompt };
}

export type ParseProposalsResult =
  | { ok: true; proposals: TileProposal[] }
  | { ok: false; error: string };

/**
 * Extracts and validates tile proposals from the AI's raw response text.
 * Tolerant of a fenced ```json block (expected) or bare JSON (fallback);
 * rejects anything that doesn't yield at least one proposal with real SQL.
 */
export function parseTileProposals(responseText: string): ParseProposalsResult {
  const fenced = responseText.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const jsonText = (fenced ? fenced[1] : responseText).trim();
  if (!jsonText) {
    return { ok: false, error: 'The AI did not return a response.' };
  }

  let data: unknown;
  try {
    data = JSON.parse(jsonText);
  } catch {
    return { ok: false, error: 'Could not understand the AI response — try rephrasing your request.' };
  }

  const proposalsRaw = (data as { proposals?: unknown } | null)?.proposals;
  if (!Array.isArray(proposalsRaw) || proposalsRaw.length === 0) {
    return { ok: false, error: 'The AI did not propose any tiles.' };
  }

  const proposals: TileProposal[] = [];
  for (let i = 0; i < proposalsRaw.length; i++) {
    const raw = proposalsRaw[i] as Record<string, unknown> | null;
    const sql = typeof raw?.sql === 'string' ? raw.sql.trim() : '';
    if (!sql) continue;
    const title = typeof raw?.title === 'string' && raw.title.trim() ? raw.title.trim() : `Tile ${i + 1}`;
    const tileType: AiTileProposalType =
      typeof raw?.tileType === 'string' && VALID_TILE_TYPES.has(raw.tileType)
        ? (raw.tileType as AiTileProposalType)
        : 'table';
    proposals.push({ id: `ai-proposal-${i}`, title, tileType, sql });
  }

  if (proposals.length === 0) {
    return { ok: false, error: 'The AI did not propose any valid SQL.' };
  }
  return { ok: true, proposals };
}
